'use client';

import { logger } from '@adornis/base/logging.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import { Loader2, Lock, Paperclip, Send } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getIconByName } from '../../lib/suggestionIcons.js';

import { getExamStateByExamIdQuery, streamExamStateByExamIdSubscription } from '../../../db/api/get-exams.js';
import { createPendingFileMutation, deletePendingFileMutation } from '../../../db/api/pending-file.js';
import { setExamUiNeedsRefreshMutation } from '../../../db/api/update-exam-state.js';
import type { Suggestion } from '../../../db/conversation.js';
import { Button } from '../../components/ui/button.js';
import { cn } from '../../lib/utils.js';
import TokenInput from '../ui/TokenInput.js';

// Import ausgelagerter Module
import { getGradingStats, getRegionStats, getSolutionStats, unwrapDoc } from './chat-helpers.js';

// --- MAIN COMPONENT ---
export type UiStepStatus = 'complete' | 'loading' | 'pending' | 'failed';

interface ChatInputProps {
  onSubmit: (message: string, opts?: { attachedFileNames?: string[] | null }) => void;
  isLoading?: boolean;
  isAgentThinking?: boolean;
  suggestions?: Suggestion[];
  suggestionsLoading?: boolean;
  examId?: string | null;
  examState?: any | null;
  onMessageClick?: (
    category?:
      | 'settings'
      | 'upload'
      | 'boundingbox'
      | 'correction'
      | 'msedit'
      | 'summary'
      | 'submissioncheck'
      | 'structureeditor',
    data?: any,
  ) => void;
  onUiRefreshRequested?: () => void | Promise<void>;
  answerBbxUncertaintyCount?: number;
  correctionUncertaintyCount?: number;
  /** A student only ever uploads their own single exam, so the "student assignment" (split) step
   * is meaningless for them and is hidden from the progress bar. */
  role?: 'teacher' | 'student';
  onStepStatusChange?: (
    statuses: UiStepStatus[],
    percents: number[],
    lowConfCounts: number[],
    counts: { finished: number; total: number }[],
    /**
     * Whether index 0 of those arrays is the exam-creator step rather than the upload step — it is only
     * present while this exam is being created/refined (see `showExamCreatorStep`). The icon sidebar in
     * chat.tsx prepends its matching icon under this same flag, which is what keeps the two aligned.
     */
    hasExamCreatorStep?: boolean,
    /** Whether the arrays carry an entry for the structure step at all — see `showStructureStep`. */
    hasStructureStep?: boolean,
  ) => void;
}

export function ChatInput({
  onSubmit,
  isLoading = true,
  isAgentThinking = false,
  suggestions = [],
  suggestionsLoading = false,
  examId,
  onMessageClick,
  onUiRefreshRequested,
  answerBbxUncertaintyCount,
  correctionUncertaintyCount,
  role,
  onStepStatusChange,
}: ChatInputProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [attachedPendingFiles, setAttachedPendingFiles] = useState<Array<{ name: string; pendingId: string }>>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Array<{ id: string; name: string }>>([]);
  const [isDragOverInput, setIsDragOverInput] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const [taskbarExamState, setTaskbarExamState] = useState<any>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const inputDragCounterRef = useRef(0);
  const isHandlingUiRefreshRef = useRef(false);

  useEffect(() => {
    if (!examId) {
      setTaskbarExamState(null);
      return;
    }

    let isMounted = true;

    const processUiRefreshRequest = (nextExamState: any) => {
      if (nextExamState?.uiNeedsRefresh !== true || isHandlingUiRefreshRef.current) return;

      isHandlingUiRefreshRef.current = true;
      void Promise.resolve(onUiRefreshRequested?.())
        .catch(error => {
          logger.error(error, `Error while handling UI refresh request:`);
        })
        .finally(() => {
          void setExamUiNeedsRefreshMutation(examId, false)()
            .catch(error => {
              logger.error(error, `Error resetting uiNeedsRefresh flag:`);
            })
            .finally(() => {
              isHandlingUiRefreshRef.current = false;
            });
        });
    };

    const examStateFields = {
      metaDataSet: 1,
      hasGradingStarted: 1,
      isCurrentlyGrading: 1,
      onlineSubmissionReceived: 1,
      masterSolutionError: 1,
      gradingSchemeError: 1,
      masterSolutionSourceState: 1,
      gradingSchemeSourceState: 1,
      gradingSchemeUncertainTaskCount: 1,
      uploadProgress: { totalFiles: 1, uploadedFiles: 1 },
      taskExtractionProgress: {
        status: 1,
        total: 1,
        // `finishedUnits`, NOT `finished`. The counter was replaced by the set of done unit ids and there is no
        // `finished` field on these types any more — asking for it returned nothing, so every bar that reads
        // `getRegionStats` sat at 0 of N while the units piled up in Mongo. See `BaseExtractionProgress`.
        finishedUnits: 1,
        message: 1,
        lowConfRegions: {
          boundingBoxId: 1,
          submissionId: 1,
          taskPath: 1,
          additionalInfo: 1,
        },
        errorRegions: {
          boundingBoxId: 1,
          submissionId: 1,
          taskPath: 1,
          additionalInfo: 1,
        },
      },
      answerExtractionProgress: {
        status: 1,
        total: 1,
        finishedUnits: 1,
        message: 1,
        errorSubmissions: 1,
        lowConfRegions: {
          boundingBoxId: 1,
          submissionId: 1,
          taskPath: 1,
          additionalInfo: 1,
        },
        errorRegions: {
          boundingBoxId: 1,
          submissionId: 1,
          taskPath: 1,
          additionalInfo: 1,
        },
      },
      masterSolutionProgress: {
        status: 1,
        producer: 1,
        total: 1,
        createdForTasks: 1,
        message: 1,
        lowConfRegions: {
          boundingBoxId: 1,
          submissionId: 1,
          taskPath: 1,
          additionalInfo: 1,
        },
        errorRegions: {
          boundingBoxId: 1,
          submissionId: 1,
          taskPath: 1,
          additionalInfo: 1,
        },
      },
      gradingSchemeProgress: {
        status: 1,
        producer: 1,
        total: 1,
        createdForTasks: 1,
        message: 1,
        lowConfRegions: {
          boundingBoxId: 1,
          submissionId: 1,
          taskPath: 1,
          additionalInfo: 1,
        },
        errorRegions: {
          boundingBoxId: 1,
          submissionId: 1,
          taskPath: 1,
          additionalInfo: 1,
        },
      },
      gradingProgress: { status: 1, total: 1, finishedUnits: 1, message: 1 },
      splitConfirmationState: { status: 1, confirmed: 1, message: 1, total: 1, finished: 1 },
      // The exam creator's own progress, read from the SAME state every other step here reads — that is
      // what makes its ring fill live. `phase` is which of PLAN/MATERIAL/SOURCE/REPAIR is currently
      // running (see `ExamCreationPhase` in confirmation-progress.ts), and `total`/`finished` are always
      // relative to THAT phase's own unit of work. `pdfPersisted` is REPAIR's own PDF-compile sub-checkpoint.
      // See `examCreatorPercent` below, which turns all four into one ring position.
      examCreationState: { status: 1, confirmed: 1, message: 1, phase: 1, total: 1, finished: 1, pdfPersisted: 1 },
      structureConfirmationState: { status: 1, confirmed: 1, message: 1 },
      uiNeedsRefresh: 1,
    } as const;

    void getExamStateByExamIdQuery(examId)(examStateFields)
      .then(nextExamState => {
        if (!isMounted) return;
        const unwrapped = unwrapDoc(nextExamState);
        setTaskbarExamState(unwrapped);
        processUiRefreshRequest(unwrapped);
      })
      .catch(error => {
        logger.error(error, `Error loading taskbar exam state:`);
      });

    const subscription = streamExamStateByExamIdSubscription(examId)(examStateFields).subscribe({
      next: nextExamState => {
        const unwrapped = unwrapDoc(nextExamState);
        setTaskbarExamState(unwrapped);
        processUiRefreshRequest(unwrapped);
      },
      error: error => {
        logger.error(error, `Error streaming taskbar exam state:`);
      },
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [examId, onUiRefreshRequested]);

  // POLLED SEPARATELY FROM THE SUBSCRIPTION ABOVE, and deliberately not trusted to it: streamExamStateByExamIdSubscription
  // is the exact subscription chat.tsx documents as carrying a client-side caching bug in @adornis/baseql
  // (SubscriptionDedupe + completingShareReplay can freeze a shared subscription on its first-ever value
  // after a resubscribe — confirmed there via targeted logging to leave a field permanently stale). That
  // comment's own fix is a plain one-off query on a reliable trigger, because a one-off query has no such
  // caching path. There is no other reliable trigger here for a value that changes every couple of
  // seconds while a creation runs, so this polls for it directly instead — the same workaround, applied
  // the only way it can be for a counter with no natural external trigger to piggyback on.
  const examCreationStatusForPolling = taskbarExamState?.examCreationState?.status;
  useEffect(() => {
    if (!examId) return;
    if (examCreationStatusForPolling !== 'STARTED' && examCreationStatusForPolling !== 'RUNNING') return;

    let cancelled = false;
    const poll = () => {
      void getExamStateByExamIdQuery(examId)({
        examCreationState: { status: 1, confirmed: 1, message: 1, phase: 1, total: 1, finished: 1, pdfPersisted: 1 },
      } as any)
        .then(freshExamState => {
          if (cancelled) return;
          const freshExamCreationState = (freshExamState as any)?.examCreationState;
          if (!freshExamCreationState) return;
          setTaskbarExamState((prev: any) => (prev ? { ...prev, examCreationState: freshExamCreationState } : prev));
        })
        .catch(error => {
          logger.error(error, `Failed to poll exam-creation progress:`);
        });
    };

    poll();
    const intervalId = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [examId, examCreationStatusForPolling]);

  const effectiveExamState = taskbarExamState;
  const canUpload = true;

  const mapProgressStatus = (
    progress?: { status?: string; total?: number; finished?: number } | null,
  ): UiStepStatus => {
    if (!progress) return 'pending';
    if (progress.status === 'ERROR') return 'failed';
    if (progress.status === 'COMPLETED') return 'complete';
    // Enqueued behind a gate, nothing running — the `total` fallback below would otherwise read it as loading.
    if (progress.status === 'QUEUED') return 'pending';
    if (progress.status === 'IN_PROGRESS') return 'loading';
    if ((progress.finished ?? 0) > 0 || (progress.total ?? 0) > 0) return 'loading';
    return 'pending';
  };

  const mapConfirmationStatus = (progress?: { status?: string; confirmed?: boolean } | null): UiStepStatus => {
    if (!progress) return 'pending';
    if (progress.status === 'FAILED') return 'failed';
    if (progress.status === 'DONE' || progress.confirmed === true) return 'complete';
    if (progress.status === 'STARTED' || progress.status === 'RUNNING') return 'loading';
    return 'pending';
  };

  const metadataStatus: UiStepStatus = effectiveExamState?.metaDataSet ? 'complete' : 'pending';
  // Hoisted, because both the step's STATUS and the step's displayed counts are built from them — and computed
  // three times below before this.
  const taskStats = getRegionStats(effectiveExamState?.taskExtractionProgress);
  const answerStats = getRegionStats(effectiveExamState?.answerExtractionProgress);
  // Through the stats, like every other step below: `mapProgressStatus` wants a `finished` NUMBER and these two
  // records carry a set of unit ids, so handing it the raw record left its count branch reading 0.
  const rawTaskExtractionStatus = mapProgressStatus({
    status: effectiveExamState?.taskExtractionProgress?.status,
    total: taskStats.total,
    finished: taskStats.finished,
  });
  // Extraction finishing is not the same as the teacher having confirmed the result — without this, the
  // sidebar's "focus" (the breathing/active icon in StepsIconSidebar, driven by status === 'loading') moves off
  // structure the instant the background OCR pass completes, even though the confirmation card the teacher still
  // has to act on may not have posted yet (see `structureAnnounce`'s own wait on the split being confirmed).
  const taskExtractionStatus: UiStepStatus =
    rawTaskExtractionStatus === 'complete' && effectiveExamState?.structureConfirmationState?.confirmed !== true
      ? 'loading'
      : rawTaskExtractionStatus;
  // An online submission never runs answer extraction at all — see student-exam.ts's own header
  // ("taskSubmissions populated on insert because there is nothing to extract"), so
  // `answerExtractionProgress` would otherwise sit at its untouched default forever, same bug the "upload"
  // step had. The answers ARE already final the instant they're handed in, so "complete" is the correct
  // reading, not just a hidden/skipped step like "upload".
  const answerExtractionStatus: UiStepStatus = effectiveExamState?.onlineSubmissionReceived
    ? 'complete'
    : mapProgressStatus({
        status: effectiveExamState?.answerExtractionProgress?.status,
        total: answerStats.total,
        finished: answerStats.finished,
      });
  // How a solution step words itself.
  //
  // WHAT IS RUNNING WINS, and the state itself says who is running it (`producer`) — the stored provenance
  // only flips once a run has finished writing its tasks, so a re-upload onto a generated solution would
  // otherwise still read 'generated' for the whole extraction, showing the teacher no sign that their
  // upload is being processed at all.
  //
  // With nothing running, the roll-up describes what is there, because a solution can be BOTH: uploaded for
  // most tasks and generated for the rest. `undefined` (or 'manual') means there is no roll-up to go on —
  // then the producer of the last run still says which way the exam is heading, and with no run at all
  // "wird erstellt" is the honest default (there is nothing uploaded to detect).
  const pickSolutionView = (
    sourceState: 'upload' | 'generated' | 'manual' | 'mixed' | undefined | null,
    progress: { status?: string; producer?: string } | undefined,
  ): 'detect' | 'create' | 'mixed' => {
    // A REFINE FALLS THROUGH to the roll-up below, because it is neither: it is correcting an artifact that is
    // already there, so what is there is the honest thing to show. Reading it as a producer would label a refine of an
    // uploaded solution "wird erstellt".
    if (progress?.status === 'IN_PROGRESS' && progress.producer !== 'refine')
      return progress.producer === 'generation' ? 'create' : 'detect';
    if (sourceState === 'upload') return 'detect';
    if (sourceState === 'generated') return 'create';
    if (sourceState === 'mixed') return 'mixed';
    return progress?.producer === 'extraction' ? 'detect' : 'create';
  };

  const msView = pickSolutionView(
    effectiveExamState?.masterSolutionSourceState,
    effectiveExamState?.masterSolutionProgress,
  );
  const gsView = pickSolutionView(
    effectiveExamState?.gradingSchemeSourceState,
    effectiveExamState?.gradingSchemeProgress,
  );

  // One state per solution, so the status comes from one place regardless of which producer filled it.
  const masterSolutionStats = getSolutionStats(effectiveExamState?.masterSolutionProgress);
  // lowConfCount is REPLACED (not added to) with the deduped cross-signal count — gradingSchemeUncertainTaskCount
  // already folds lowConfRegions into its per-task union, so summing the two would double-count a task that
  // trips both a low-confidence region AND e.g. zero points.
  const gradingSchemeStats = {
    ...getSolutionStats(effectiveExamState?.gradingSchemeProgress),
    lowConfCount: Number(effectiveExamState?.gradingSchemeUncertainTaskCount ?? 0),
  };

  let masterSolutionStatus: UiStepStatus = mapProgressStatus({
    status: effectiveExamState?.masterSolutionProgress?.status,
    total: masterSolutionStats.total,
    finished: masterSolutionStats.finished,
  });

  // If an upload error was recorded for the master solution, mark the step as failed
  if (effectiveExamState?.masterSolutionError) {
    masterSolutionStatus = 'failed';
  }
  const gradingSchemeStatus: UiStepStatus = mapProgressStatus({
    status: effectiveExamState?.gradingSchemeProgress?.status,
    total: gradingSchemeStats.total,
    finished: gradingSchemeStats.finished,
  });
  // Through the stats, like every other step: `mapProgressStatus` wants a `finished` NUMBER and a progress
  // record carries a set of unit ids. Handing it the raw record made its count fall back to 0.
  const gradingStats = getGradingStats(effectiveExamState?.gradingProgress);
  const gradingStatus: UiStepStatus = mapProgressStatus({
    status: effectiveExamState?.gradingProgress?.status,
    total: gradingStats.total,
    finished: gradingStats.finished,
  });
  const splitConfirmationStatus: UiStepStatus = mapConfirmationStatus(effectiveExamState?.splitConfirmationState);

  // PENDING (the default on every exam, uploaded or created) means the teacher never asked to create or
  // refine one — see the exam-creator step's own comment below for the full reasoning. Computed here,
  // ahead of `examCreatorStep`, because `showStructureStep` needs it as its generated-exam signal.
  const showExamCreatorStep =
    effectiveExamState?.examCreationState?.status !== undefined &&
    effectiveExamState?.examCreationState?.status !== 'PENDING';

  // The structure step describes reading the tasks off an UPLOADED sheet — a generated exam never does
  // that (see structure.job.ts's own `generatedExam` drop; its own task tree is auto-confirmed the
  // moment creation succeeds instead). At the start of a conversation we do not yet know which kind
  // this exam will become, so the step stays hidden until extraction actually starts on a non-generated
  // one. `showExamCreatorStep` is the generated-exam signal available here: this component only reads
  // `examState`, not `Exam.examSource`, and `examCreationState` is documented as untouched by an
  // uploaded exam — so once the exam turns out to be generated the step never has a reason to appear.
  const taskStatusRaw = effectiveExamState?.taskExtractionProgress?.status as string | undefined;
  const showStructureStep = !showExamCreatorStep && taskStatusRaw !== undefined && taskStatusRaw !== 'NOT_STARTED';

  const progressStatuses: UiStepStatus[] = [
    metadataStatus,
    splitConfirmationStatus,
    ...(showStructureStep ? [taskExtractionStatus] : []),
    masterSolutionStatus,
    gradingSchemeStatus,
    answerExtractionStatus,
    gradingStatus,
  ];
  const completedCount = progressStatuses.filter(status => status === 'complete').length;
  const percent = Math.round((completedCount / progressStatuses.length) * 100);

  const emptyStats = { lowConfCount: 0, errorCount: 0, errorSubCount: 0, message: '', total: 0, finished: 0 };
  const answerUncertaintyCount = Number(answerBbxUncertaintyCount ?? 0);
  const correctionUncertaintyCountValue = Number(correctionUncertaintyCount ?? 0);

  // ── THE EXAM CREATOR, the one step that is not part of the upload pipeline ──
  //
  // Deliberately built exactly like the confirmation steps: it is a `ConfirmationProgress` on the exam
  // state, so it goes through the same `mapConfirmationStatus`. Unlike every other step, though, it also
  // carries an explicit precomputed `percent` (see `examCreatorPercent` below) instead of relying on the
  // generic `stats.total`/`stats.finished` ratio the roll-up uses for everything else — because `total`/
  // `finished` are relative to whichever of PLAN/MATERIAL/SOURCE/REPAIR is currently running (see
  // `ExamCreationPhase` in confirmation-progress.ts), not to the run as a whole, so that ratio alone cannot
  // place the ring across all four phases. `stepPercents` below prefers this field when present.
  //
  // `showExamCreatorStep` itself is computed further up, ahead of `progressStatuses` — see its own
  // comment there for why (PENDING is the default on every exam, uploaded or created, and is what keeps
  // this step out of the bar for an ordinary uploaded exam; once started it never goes back).
  const examCreationState = effectiveExamState?.examCreationState;
  const examCreationPhase = examCreationState?.phase as string | undefined;
  const examCreationTotal = Number(examCreationState?.total ?? 0);
  const examCreationFinished = Number(examCreationState?.finished ?? 0);
  const examCreationPhaseFraction = examCreationTotal > 0 ? Math.min(1, examCreationFinished / examCreationTotal) : 0;

  // ONE EQUAL SLICE PER PHASE, in the order `runExamCreation` always runs them — no attempt to weight a
  // phase's slice by how long it actually tends to take, so the ring simply reads "which of the 4 phases,
  // and how far through it" rather than an estimate of overall wall-clock progress. REPAIR's own slice
  // leaves room above its `finished`/`total` (repair-round) fraction for the PDF-compile sub-checkpoint
  // below; 100% itself is handled by the generic `s.status === 'complete'` branch once the job flips the
  // whole step to DONE.
  const EXAM_CREATOR_PHASES = ['PLAN', 'MATERIAL', 'SOURCE', 'REPAIR'] as const;
  const examCreatorPhaseIndex = examCreationPhase ? EXAM_CREATOR_PHASES.indexOf(examCreationPhase as (typeof EXAM_CREATOR_PHASES)[number]) : -1;
  const examCreatorSliceSize = 100 / EXAM_CREATOR_PHASES.length;
  const [examCreatorBandStart, examCreatorBandEnd] =
    examCreatorPhaseIndex >= 0
      ? [examCreatorPhaseIndex * examCreatorSliceSize, (examCreatorPhaseIndex + 1) * examCreatorSliceSize]
      : [0, 0];
  const examCreatorPercent =
    examCreationPhase === 'REPAIR' && examCreationState?.pdfPersisted
      ? 98
      : Math.round(examCreatorBandStart + examCreationPhaseFraction * (examCreatorBandEnd - examCreatorBandStart));

  const EXAM_CREATOR_PHASE_LABEL_KEY: Record<string, string> = {
    PLAN: 'ChatInput.steps.examCreator.phasePlan',
    MATERIAL: 'ChatInput.steps.examCreator.phaseMaterial',
    SOURCE: 'ChatInput.steps.examCreator.phaseSource',
    REPAIR: 'ChatInput.steps.examCreator.phaseRepair',
  };
  const examCreatorActiveLabelKey = examCreationPhase ? EXAM_CREATOR_PHASE_LABEL_KEY[examCreationPhase] : undefined;

  const examCreatorStep = {
    label: t('ChatInput.steps.examCreator.label'),
    ...(examCreatorActiveLabelKey ? { activeLabel: t(examCreatorActiveLabelKey) } : {}),
    status: mapConfirmationStatus(examCreationState),
    category: 'examcreator' as const,
    percent: examCreatorPercent,
    stats: {
      ...emptyStats,
      message: String(examCreationState?.message ?? ''),
      total: examCreationTotal,
      finished: examCreationFinished,
    },
    messages: [],
  };

  const steps = [
    // FIRST, ahead of the upload pipeline, and only while it applies — chat.tsx's icon sidebar prepends its
    // matching icon under the same flag (`hasExamCreatorStep`, passed up by `onStepStatusChange` below), so
    // the two arrays stay index-aligned.
    ...(showExamCreatorStep ? [examCreatorStep] : []),
    {
      label: t('ChatInput.steps.upload.label'),
      status:
        effectiveExamState?.uploadProgress?.totalFiles === effectiveExamState?.uploadProgress?.uploadedFiles &&
        effectiveExamState?.uploadProgress?.totalFiles > 0
          ? 'complete'
          : 'pending',
      // Meaningless once the answers arrived through the online student-exam flow instead — see
      // `onlineSubmissionReceived` (nothing was ever "uploaded" for this exam, so the step would otherwise
      // sit on "pending" forever). Filtered out below by this category, same way `submissioncheck` already
      // is for a student — chat.tsx's icon sidebar filters the same category to stay index-aligned.
      category: 'upload' as const,
      stats: {
        total: effectiveExamState?.uploadProgress?.totalFiles,
        finished: effectiveExamState?.uploadProgress?.uploadedFiles,
        lowConfCount: 0,
        errorCount: 0,
        errorSubCount: 0,
        message: '',
      },
      messages: [],
    },
    {
      label: t('ChatInput.steps.studentAssignment.label'),
      activeLabel:
        Number(effectiveExamState?.splitConfirmationState?.total ?? 0) ===
          Number(effectiveExamState?.splitConfirmationState?.finished ?? 0) &&
        Number(effectiveExamState?.splitConfirmationState?.total ?? 0) > 0
          ? t('ChatInput.steps.studentAssignment.activeWait')
          : t('ChatInput.steps.studentAssignment.activeAssign'),
      status: splitConfirmationStatus,
      category: 'submissioncheck' as const,
      stats: {
        ...emptyStats,
        message: String(effectiveExamState?.splitConfirmationState?.message ?? ''),
        total: Number(effectiveExamState?.splitConfirmationState?.total ?? 0),
        finished: Number(effectiveExamState?.splitConfirmationState?.finished ?? 0),
      },
      messages: [],
    },
    // Only while it applies — see `showStructureStep`'s own comment above. chat.tsx's icon sidebar drops
    // the matching `structureeditor` icon under the same flag, which is what keeps the two arrays aligned.
    ...(showStructureStep
      ? [
          {
            label: t('ChatInput.steps.structure.label'),
            activeLabel: t('ChatInput.steps.structure.active'),
            status: taskExtractionStatus,
            category: 'structureeditor' as const,
            stats: taskStats,
            messages: [],
          },
        ]
      : []),
    {
      label: t('ChatInput.steps.answers.label'),
      activeLabel: t('ChatInput.steps.answers.active'),
      status: answerExtractionStatus,
      category: 'correction' as const,
      stats: {
        ...answerStats,
        lowConfCount: answerUncertaintyCount >= 0 ? answerUncertaintyCount : answerStats.lowConfCount,
      },
      canvasMode: 'answers' as const,
      messages: [],
    },
    {
      label: t('ChatInput.steps.masterSolution.label'),
      activeLabel:
        msView === 'mixed'
          ? t('ChatInput.steps.masterSolution.activeComplete')
          : msView === 'detect'
            ? t('ChatInput.steps.masterSolution.activeDetect')
            : t('ChatInput.steps.masterSolution.activeCreate'),
      status: masterSolutionStatus,
      category: 'msedit' as const,
      stats: masterSolutionStats,
      messages: [],
    },
    {
      label: t('ChatInput.steps.gradingScheme.label'),
      activeLabel:
        gsView === 'mixed'
          ? t('ChatInput.steps.gradingScheme.activeComplete')
          : gsView === 'detect'
            ? t('ChatInput.steps.gradingScheme.activeDetect')
            : t('ChatInput.steps.gradingScheme.activeCreate'),
      status: gradingSchemeStatus,
      category: 'msedit' as const,
      stats: gradingSchemeStats,
      messages: [],
    },
    {
      label: t('ChatInput.steps.grading.label'),
      activeLabel: t('ChatInput.steps.grading.active'),
      status: gradingStatus,
      category: 'correction' as const,
      stats: {
        ...getGradingStats(effectiveExamState?.gradingProgress),
        lowConfCount:
          effectiveExamState?.gradingProgress?.status && effectiveExamState?.gradingProgress?.status !== 'PENDING'
            ? correctionUncertaintyCountValue >= 0
              ? correctionUncertaintyCountValue
              : getGradingStats(effectiveExamState?.gradingProgress).lowConfCount
            : 0,
      },
      canvasMode: 'correction' as const,
      messages: [],
    },
    {
      label: t('ChatInput.steps.summary.label'),
      // Reuses the same content+language-use uncertainty count as the Grading step above (see
      // correctionUncertaintyCount in chat.tsx) instead of a separate, content-only computation.
      status: correctionUncertaintyCountValue === 0 && gradingStatus === 'complete' ? 'complete' : 'pending',
      category: 'summary' as const,
      stats: emptyStats,
      messages: [],
    },
  ];

  // A student only ever uploads their own single exam — the "student assignment" (split) step is
  // meaningless for them, so it's dropped before anything downstream zips these steps by index
  // with chat.tsx's StepsIconSidebar (which filters the same way, on the same predicate).
  //
  // The "upload" step is dropped once the answers arrived online instead of by upload — see the
  // `category: 'upload'` step's own comment above. Same predicate chat.tsx's icon sidebar applies, so the
  // two stay index-aligned.
  const visibleSteps = steps.filter(
    s =>
      (role !== 'student' || s.category !== 'submissioncheck') &&
      (!effectiveExamState?.onlineSubmissionReceived || s.category !== 'upload'),
  );

  const activeProcesses = visibleSteps.filter(step => step.status === 'loading');

  const stepStatuses = visibleSteps.map(s => s.status as UiStepStatus);
  const stepPercents = visibleSteps.map(s => {
    if (s.status === 'complete') return 100;
    // The exam-creator step precomputes its own two-phase percent (see `examCreatorPercent` above) since
    // its `stats.total`/`stats.finished` alone cannot represent the PDF-compile phase. Every other step
    // has no `percent` field and falls through to the generic ratio below, unchanged.
    const explicitPercent = (s as { percent?: number }).percent;
    if (typeof explicitPercent === 'number') return explicitPercent;
    const total = s.stats?.total ?? 0;
    const finished = s.stats?.finished ?? 0;
    return total > 0 ? Math.min(100, Math.round((finished / total) * 100)) : 0;
  });
  const stepLowConfs = visibleSteps.map(s => s.stats?.lowConfCount ?? 0);
  const stepCounts = visibleSteps.map(s => ({
    finished: s.stats?.finished ?? 0,
    total: s.stats?.total ?? 0,
  }));
  useEffect(() => {
    onStepStatusChange?.(stepStatuses, stepPercents, stepLowConfs, stepCounts, showExamCreatorStep, showStructureStep);
  }, [
    stepStatuses.join(','),
    stepPercents.join(','),
    stepLowConfs.join(','),
    stepCounts.map(c => `${c.finished}/${c.total}`).join(','),
    showExamCreatorStep,
    showStructureStep,
  ]);

  const inlineSuggestions = suggestions.slice(0, 6);

  const hasPendingUploads = isUploadingFile || uploadingFiles.length > 0;
  const hasInputToSend = input.trim().length > 0 || attachedPendingFiles.length > 0;
  const canSendMessage = !isLoading && !isAgentThinking && !hasPendingUploads && hasInputToSend;

  const sendMessage = (text: string) => {
    if (hasPendingUploads) return;
    const hasText = text.trim().length > 0;
    const hasAttachments = attachedPendingFiles.length > 0;
    if (!hasText && !hasAttachments) return;
    onSubmit(text, {
      attachedFileNames: attachedPendingFiles.map(file => `${file.pendingId}:${encodeURIComponent(file.name)}`),
    });
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setAttachedPendingFiles([]);
  };

  const handleFileSelect = async (files: FileList | null) => {
    const selectedFiles = files ? Array.from(files) : [];
    if (selectedFiles.length === 0) return;

    const filesToUpload = selectedFiles.map((file, index) => ({
      file,
      tempId: `${file.name}-${Date.now()}-${index}`,
    }));

    setIsUploadingFile(true);
    setUploadingFiles(filesToUpload.map(({ tempId, file }) => ({ id: tempId, name: file.name })));

    try {
      if (!examId) {
        throw new Error(t('ChatInput.alerts.noExamId'));
      }

      for (const { file, tempId } of filesToUpload) {
        let uploadedIds: string[] = [];
        if (file.type === 'application/pdf') {
          const pdfId = await PDFFile.upload(file);
          uploadedIds = [pdfId];
        } else {
          const imgId = await ImageFile.upload(file);
          uploadedIds = [imgId];
        }

        const pendingId = await createPendingFileMutation(examId, uploadedIds, file.name, file.type, file.size)();

        setAttachedPendingFiles(prev => [...prev, { name: file.name, pendingId: String(pendingId) }]);
        setUploadingFiles(prev => prev.filter(item => item.id !== tempId));
      }
    } catch (err) {
      logger.error(err, `Upload failed`);
      alert(t('ChatInput.alerts.uploadError'));
    } finally {
      setIsUploadingFile(false);
      setUploadingFiles([]);
    }
  };

  const isFileDrag = (event: React.DragEvent<HTMLElement>) =>
    Array.from(event.dataTransfer?.types ?? []).includes('Files');

  const resetInputDragState = () => {
    inputDragCounterRef.current = 0;
    setIsDragOverInput(false);
  };

  const handleInputDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    inputDragCounterRef.current += 1;
    setIsDragOverInput(true);
  };

  const handleInputDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    if (!isDragOverInput) setIsDragOverInput(true);
  };

  const handleInputDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    inputDragCounterRef.current = Math.max(0, inputDragCounterRef.current - 1);
    if (inputDragCounterRef.current === 0) {
      setIsDragOverInput(false);
    }
  };

  const handleInputDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    const droppedFiles = event.dataTransfer.files;
    resetInputDragState();
    void handleFileSelect(droppedFiles);
  };

  return (
    <div className="pt-2 pb-0 sm:pt-0 sm:pb-0 border-t border-border bg-background relative min-w-0 overflow-x-hidden px-1">
      <div className="max-w-4xl relative mx-auto">
        <div className="mt-2 mb-2 px-1 overflow-hidden">
          <div className="flex flex-row flex-nowrap items-center justify-center gap-1.5">
            {!isAgentThinking && suggestionsLoading ? (
              <div key="inline-suggestions-loading" className="flex items-center px-2 py-1 border border-transparent">
                <Loader2 className="h-4 w-4 text-brand-strong animate-spin" />
              </div>
            ) : !isAgentThinking && inlineSuggestions.length > 0 ? (
              inlineSuggestions.map((suggestion, index) => (
                <div key={`inline-suggestion-${index}-${suggestion.label}`} className="group relative min-w-0">
                  <button
                    type="button"
                    onClick={() => {
                      if (suggestion.isUpload) {
                        fileInputRef.current?.click();
                      }
                      setInput(suggestion.prompt ?? suggestion.label ?? '');
                    }}
                    className="w-full min-w-0 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground truncate hover:bg-brand-strong hover:border-brand-strong hover:text-white transition-colors"
                    title={suggestion.label ?? undefined}
                  >
                    <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                      {(() => {
                        const Icon = getIconByName(suggestion.icon ?? undefined);
                        if (Icon) return <Icon className="h-4 w-4 text-current shrink-0" />;
                        return null;
                      })()}
                      <span className="truncate min-w-0">{suggestion.label}</span>
                    </div>
                  </button>
                  <div className="pointer-events-none absolute bottom-full left-0 z-30 mb-1.5 w-max max-w-[min(36rem,75vw)] whitespace-normal break-words rounded-lg border border-border bg-popover px-2 py-1 text-xs font-medium text-popover-foreground opacity-0 duration-0 group-hover:opacity-100">
                    {suggestion.prompt}
                  </div>
                </div>
              ))
            ) : (
              <div className="invisible px-2.5 py-1 text-xs font-medium border border-transparent" aria-hidden="true">
                &nbsp;
              </div>
            )}
          </div>
        </div>

        <form
          onSubmit={e => {
            e.preventDefault();
            if (!canSendMessage) return;
            sendMessage(input);
          }}
          className="flex gap-2 sm:gap-3 items-end w-full"
        >
          <div
            onDragEnter={handleInputDragEnter}
            onDragOver={handleInputDragOver}
            onDragLeave={handleInputDragLeave}
            onDrop={handleInputDrop}
            className={cn(
              'relative flex-1 flex flex-col gap-2 bg-muted rounded-3xl px-3 py-2 sm:py-3 focus-within:ring-2 focus-within:ring-brand-primary/50 transition-all duration-200 ease-in-out',
              isDragOverInput && 'bg-brand-surface ring-2 ring-brand-primary',
            )}
          >
            {isDragOverInput && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-3xl border-2 border-dashed border-brand-strong bg-brand-surface/85">
                <span className="text-sm font-medium text-brand-text">{t('ChatInput.ui.dropFilesHere')}</span>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={e => {
                void handleFileSelect(e.target.files);
                if (e.target) e.currentTarget.value = '';
              }}
            />

            {(attachedPendingFiles.length > 0 || uploadingFiles.length > 0) && (
              <div className="flex flex-wrap items-center gap-2 w-full pt-1">
                {uploadingFiles.map(file => (
                  <div
                    key={file.id}
                    className="flex items-center gap-2 bg-background border border-border rounded-md px-2 py-1"
                  >
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin" />
                      <span className="text-xs font-medium text-muted-foreground max-w-[140px] truncate">
                        {file.name}
                      </span>
                    </div>
                  </div>
                ))}
                {attachedPendingFiles.map(file => (
                  <div
                    key={file.pendingId}
                    className="flex items-center gap-2 bg-background border border-border rounded-md px-2 py-1 shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <svg className="h-4 w-4 text-brand-text" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path
                          d="M21.44 11.05l-8.49 8.49a5 5 0 0 1-7.07-7.07l8.49-8.49a3 3 0 0 1 4.24 4.24L10.1 16.71a1 1 0 0 1-1.41-1.41l7.07-7.07"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="text-xs font-medium text-foreground max-w-[140px] truncate">{file.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        if (examId) {
                          try {
                            await deletePendingFileMutation(examId, file.pendingId)();
                          } catch (err) {
                            logger.error(err, `Error deleting pending file`);
                          }
                        }
                        setAttachedPendingFiles(prev => prev.filter(item => item.pendingId !== file.pendingId));
                      }}
                      className="text-muted-foreground hover:text-red-500 p-1 rounded-full transition-colors"
                      aria-label="Remove attached file"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-row items-end gap-2 w-full">
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground p-1.5 rounded-full hover:bg-accent transition-colors mb-0.5"
                onClick={() => canUpload && fileInputRef.current?.click()}
                disabled={!canUpload}
                title={t('ChatInput.ui.uploadFiles')}
                style={!canUpload ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
              >
                <Paperclip className="h-5 w-5" />
              </button>

              <div className="flex-1 min-w-0">
                <TokenInput
                  ref={textareaRef}
                  value={input}
                  isLoading={isLoading || isAgentThinking || hasPendingUploads}
                  onChange={setInput}
                  onSubmit={finalValue => {
                    if (
                      !isLoading &&
                      !isAgentThinking &&
                      !hasPendingUploads &&
                      (finalValue.trim() || attachedPendingFiles.length > 0)
                    ) {
                      sendMessage(finalValue);
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <span
            className="inline-flex shrink-0"
            title={!canSendMessage && hasPendingUploads ? t('ChatInput.ui.waitUpload') : t('ChatInput.ui.sendMessage')}
          >
            <Button
              type="submit"
              disabled={!canSendMessage}
              size="icon"
              className="rounded-full h-10 w-10 sm:h-11 sm:w-11 bg-brand-strong text-white hover:bg-brand-700 mb-1"
            >
              <Send className="h-4 w-4" />
            </Button>
          </span>
        </form>

        <div className="flex justify-center items-center gap-1.5 mt-2.5 opacity-80 hover:opacity-100 transition-opacity">
          <Lock className="w-3 h-3 text-muted-foreground" />
          <p className="text-[10px] text-muted-foreground text-center">
            {t('ChatInput.ui.privacyNotice')}{' '}
            <a
              href="https://gradias.ai/data-privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-text hover:underline ml-0.5"
            >
              {t('ChatInput.ui.learnMore')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
